import type { QueryRowType } from "@rocicorp/zero";
import { useEffect, useMemo, useState } from "react";
import { lastPersonalChatMessageAt } from "../../../shared/personalChatInactivity";
import type { queries } from "../../../shared/queries";
import { Button } from "../Button";
import { Modal } from "../Modal";
import { Text } from "../Text";
import {
  ModalContent,
  ModalBody,
  ModalFooter,
  ModalList,
  ModalTitleBlock,
  PersonalChatSelectableListRow,
} from "./PersonalChatListModal";

type ClosedPersonalChat = QueryRowType<
  ReturnType<typeof queries.closedPersonalChats>
>;

type Props = {
  open: boolean;
  onClose: () => void;
  onRecover: (ids: readonly string[]) => void;
  closedChats: readonly ClosedPersonalChat[];
  closedChatsComplete: boolean;
  readOnly?: boolean;
};

export function PersonalChatHistoryModal({
  open,
  onClose,
  onRecover,
  closedChats,
  closedChatsComplete,
  readOnly,
}: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) setSelected(new Set());
  }, [open]);

  const sortedChats = useMemo(
    () =>
      [...closedChats].sort(
        (a, b) =>
          lastPersonalChatMessageAt(b.chat?.entries, b.createdAt) -
          lastPersonalChatMessageAt(a.chat?.entries, a.createdAt)
      ),
    [closedChats]
  );

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleRecover = () => {
    onRecover([...selected]);
    setSelected(new Set());
  };

  const noneSelected = selected.size === 0;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContent>
        <ModalTitleBlock>
          <Text variant="text-regular-md" color="var(--text-primary)">
            History
          </Text>
          {sortedChats.length > 0 ? (
            <Text variant="text-regular" color="var(--text-secondary)">
              Closed chats you can reopen anytime.
            </Text>
          ) : closedChatsComplete ? (
            <Text variant="text-regular" color="var(--text-secondary)">
              No closed chats yet.
            </Text>
          ) : null}
        </ModalTitleBlock>
        {sortedChats.length > 0 ? (
          <ModalBody>
            <ModalList>
              {sortedChats.map((chat) => (
                <PersonalChatSelectableListRow
                  key={chat.id}
                  id={chat.id}
                  name={chat.name}
                  selected={selected.has(chat.id)}
                  onToggle={() => toggle(chat.id)}
                />
              ))}
            </ModalList>
            <ModalFooter>
              <Button
                size="l"
                disabled={readOnly || noneSelected}
                onClick={handleRecover}
              >
                {selected.size === sortedChats.length && sortedChats.length > 1
                  ? "Recover all"
                  : "Recover"}
              </Button>
            </ModalFooter>
          </ModalBody>
        ) : null}
      </ModalContent>
    </Modal>
  );
}
